import dayjs from 'dayjs';
import isPlainObject from 'lodash/isPlainObject';

// ?? TODO https://stackoverflow.com/questions/20325763/browser-sessionstorage-share-between-tabs

export enum KeyType {
    DONT_ASK = 'dontask',
}

export enum LocalStorageKeys {
    LAZY_RELOAD = 'page-has-been-force-refreshed',
    COLOR_PALETTE = 'colors',
    DEVICE_FINGERPRINT = 'tt_fp',
    BACK_URL = 'back_url',
    ACCOUNT_USER_ID = 'account_user_id',
    YM_REGISTRATION_CONFIRMED = 'ym_registration_confirmed',
}

export type LocalKey =
    | LocalStorageKeys
    | `task_${string}_${string}`
    | `question_${string}_timer`
    | `question_details`
    | `new_question_details`
    | `${string}-invalid-password`
    | `${string}-invalid-password_new`
    | `answers__${string}`
    | `new_answers__${string}`
    | `bundles_random_${string}`
    | `new_bundles_random_${string}`
    | `pages_random_${string}`
    | `timer-${string}`
    | `new_new_timer-${string}`
    | `new_new_timer_expired-${string}`
    | `new_new_timer_response-${string}`
    | `survey_${string}_use_filters`
    | `survey_${string}_question_answers`
    | `survey_${string}_link_params`
    | `survey_${string}_date_filters`
    | `survey_${string}_results_lang`
    | `survey_${string}_design_view`
    | `survey_complete_${string}`
    | `preview_${string}`
    | 'superadmin'
    | `new_survey_complete_${string}`
    | `preview_${string}`
    | `preview_device_mode`
    | `preview_view_mode`
    | `preview_required_questions_mode`
    | `lang`
    | 'account_user_id'
    | `ym_goal_${string}`
    | 'ym_registration_confirmed'
    | `${number}_account_is_canary`
    | 'survey_is_canary';

const DEBUG = false;

const prepareValue = (v: any): string => (typeof v === 'string' ? v : JSON.stringify(v));
const parseValue = (v: string): any => {
    try {
        v = JSON.parse(v);
    } catch (e) {}
    return v;
};
const createTypeKey = (type: string, subKey: string): string => `${type}[${subKey}]`;

type Value = AnyObject | PrimitiveValue | Array<AnyObject | PrimitiveValue>;

export class SessionStorage {
    static get<T = Value>(key: string): T {
        try {
            const val = parseValue(sessionStorage.getItem(key));
            DEBUG && console.info(this.name, 'get', key, val);
            return val;
        } catch {
            // storage может быть недоступен (приватный режим/блокировка) — не роняем приложение
            return null;
        }
    }

    static set(key: string, val: Value): void {
        try {
            val = prepareValue(val);
            DEBUG && console.info(this.name, 'set', key, val);
            sessionStorage.setItem(key, val);
        } catch {}
    }

    static setWithType(type: KeyType, subKey: string, val: Value): void {
        try {
            const key = createTypeKey(type, subKey);
            val = prepareValue(val);
            DEBUG && console.info(this.name, 'setWithType', key, val);
            sessionStorage.setItem(key, val);
        } catch {}
    }

    static getWithType(type: KeyType, subKey: string): Value {
        try {
            const key = createTypeKey(type, subKey);
            const val = parseValue(sessionStorage.getItem(key));
            DEBUG && console.info(this.name, 'getWithType', key, val);
            return val;
        } catch {
            return null;
        }
    }

    static remove(key: string): void {
        try {
            DEBUG && console.info(this.name, 'remove', key);
            sessionStorage.removeItem(key);
        } catch {}
    }
}

export class LocalStorage {
    static get<T = Value>(key: LocalKey): T {
        try {
            const val = parseValue(localStorage.getItem(key));
            DEBUG && console.info(this.name, 'get', key, val);
            if (val && val.expire) {
                const now = dayjs().unix();
                if (now >= val.expire) {
                    LocalStorage.remove(key);
                    return null;
                }
                return val.val;
            }
            return val;
        } catch {
            // localStorage может быть null/недоступен (блокировка хранилища):
            // раньше падало на getItem и роняло бутстрап (FRONT-28N4/28N5).
            return null;
        }
    }

    static set<T = Value>(key: LocalKey, val: T, ttlSeconds?: number): void {
        try {
            const v = ttlSeconds
                ? prepareValue({
                      val,
                      expire: dayjs().add(ttlSeconds, 'second').unix(),
                  })
                : prepareValue(val);
            DEBUG && console.info(this.name, 'set', key, val);
            localStorage.setItem(key, v);
        } catch {}
    }

    static append<T = Value>(key: LocalKey, val: T): void {
        const values = LocalStorage.get<Record<string | number, T>>(key);
        LocalStorage.set<Record<string | number, T>>(key, {
            ...values,
            ...val,
        });
    }

    static filter<T = Value>(key: LocalKey, condition: (item: T) => boolean): AnyObject<T, number> | null {
        const values = LocalStorage.get<AnyObject<T, number>>(key);

        if (!isPlainObject(values)) return null;

        for (const k of Object.keys(values)) {
            if (!condition(values[k])) {
                delete values[k];
            }
        }

        LocalStorage.set(key, values);

        return values;
    }

    static remove(key: LocalKey): void {
        try {
            DEBUG && console.info(this.name, 'remove', key);
            localStorage.removeItem(key);
        } catch {}
    }

    static clear(): void {
        try {
            DEBUG && console.info(this.name, 'clear', localStorage);
            localStorage.clear();
        } catch {}
    }

    static clearWithExceptions(except: Array<string>): void {
        try {
            DEBUG && console.info(this.name, 'clearWithException', except, localStorage);
            if (except?.length) {
                Object.keys(localStorage).forEach((key) => {
                    if (!except.includes(key)) {
                        localStorage.removeItem(key);
                    }
                });
            } else {
                localStorage.clear();
            }
        } catch {}
    }

    static clearExpired(): void {
        try {
            for (const k of Object.keys(localStorage)) {
                LocalStorage.get(k as LocalKey);
            }
        } catch {}
    }
}
